import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import { Button, Glyphicon, Alert } from 'react-bootstrap';
import { InputFile, PageHeader, ErrorPage } from 'axway-react-ui';
import validUrl from 'valid-url';
import ImportSummaryView from './ImportSummaryView';

class APIImportView extends React.Component {
	constructor(props) {
		super(props);
		IntlComponent.init(this);
		this.resetValidation = this.resetValidation.bind(this);
		this.onFileSelect = this.onFileSelect.bind(this);
		this.onUrlSelect = this.onUrlSelect.bind(this);
		this.state = {
			validationState: null
		};
	}

	_humanReadableLimit(size) {
		const step = 1000; // Going with si units

		const units = [
			this.intl.formatMsg('B'),
			this.intl.formatMsg('KB'),
			this.intl.formatMsg('MB'),
			this.intl.formatMsg('GB')
		];
		let c = 0;
		let precision = 0;
		if (Math.abs(size) > step) {
			precision = 1;
			do {
				size /= step;
				++c;
			} while ((c < units.length - 1) && (Math.abs(size) >= step));
		}
		return this.intl.formatMsg('{size} {unit}', { size: size.toFixed(precision), unit: units[c] });
	}

	componentWillReceiveProps(props) {
		const {
			valid,
			type,
			apiSpecTypes,
			error,
			errorDetail
		} = props;
		const typeData = apiSpecTypes[type];

		if (valid !== undefined && !valid) {
			this.state.validationState = {
				severity: 'error',
				title: this.intl.formatMsg('Invalid file'),
				message: this.intl.formatMsg('The file does not contain a valid {type} specification.', { type: typeData.name })
			};
		} else if (error) {
			if (errorDetail && errorDetail.status === 413) {
				this.state.validationState = {
					severity: 'error',
					title: this.intl.formatMsg('File too large'),
					message: this.intl.formatMsg(
						'The file is too large to process. Maximum size: {size}',
						{ size: this._humanReadableLimit(window.$conf.adminRequestLimit) }
					)
				};
			} else {
				this.state.validationState = {
					severity: 'error',
					title: this.intl.formatMsg('Unexpected error'),
					message: this.intl.formatMsg('Could not import {type} file.', { type: typeData.name })
				};
			}
		}
	}

	onFileSelect(f) {
		let validationState = null;
		if (!f || Array.isArray(f)) {
			validationState = {
				severity: 'error',
				title: this.intl.formatMsg('Too many files'),
				message: this.intl.formatMsg('Please import files one at a time.')
			};
		} else if (f.size > window.$conf.adminRequestLimit) {
			validationState = {
				severity: 'error',
				title: this.intl.formatMsg('File too large'),
				message: this.intl.formatMsg(
					'The file is too large to process. Maximum size: {size}',
					{ size: this._humanReadableLimit(window.$conf.adminRequestLimit) }
				)
			};
		}
		this.setState({ validationState },
			() => !validationState && this.props.onFileSelect(f)
		);
	}

	onUrlSelect(u) {
		// Ensure it's a url
		let validationState = null;
		if (!validUrl.isWebUri(u)) {
			validationState = {
				url: {
					severity: 'error',
					message: this.intl.formatMsg('Please enter a valid URL.')
				}
			};
		}
		this.setState({ validationState },
			() => !validationState && this.props.onUrlSelect(u)
		);
	}

	resetValidation() {
		this.form && this.form.reset();
		this.setState({ validationState: null });
	}

	render() {
		const {
			valid,
			importedSpec,
			currentSpec,
			type,
			specId,
			apiSpecTypes,
			error,
			errorDetail
		} = this.props;

		const typeData = apiSpecTypes[type];
		const saveLabel = this.intl.formatMsg('Save');
		const updateLabel = this.intl.formatMsg('Update');
		const importLabel = this.intl.formatMsg('Import');
		const actionLabel = currentSpec ? updateLabel : importLabel;
		const confirmationButtonLabel = currentSpec ? updateLabel : saveLabel;
		const shouldRenderImportSummary = valid && importedSpec && !error;
		// We don't want bottom border when we are in the summary view
		const headerBorderStyle = shouldRenderImportSummary ? 'no-border-bottom' : '';

		if (importedSpec && error) {
			return <ErrorPage errorDetail={errorDetail} />;
		}

		let docLink;
		if (window.$conf.endpointsEnabled) {
			docLink = window.$conf.docUpgradeEndpoints;
		} else {
			docLink = window.$conf.docManageSpecs;
		}

		return (
			<div className="api-import-view">
				<PageHeader className={headerBorderStyle}>
					<PageHeader.Section align="left">
						<PageHeader.Title
							infoPopover={{
								className: 'api-import-info',
								description: this.intl.formatMsg('{actionLabel} a {type} specification by either selecting a file from your local filesystem or by entering a URL', { actionLabel, type: typeData.name }),
								docLink
							}}
						>
							{this.intl.formatMsg('{actionLabel} {type} specification', { actionLabel, type: typeData.name })}
						</PageHeader.Title>
					</PageHeader.Section>
					<PageHeader.Section />
					<PageHeader.Section align="right">
						<Button
							bsSize="large"
							bsStyle="link"
							className="cancel"
							onClick={this.props.onCancel}
						>
							{this.intl.formatMsg('Cancel')}
						</Button>
						{type === 'endpoint' && (
							<Button
								key="save-and-mock"
								className="save"
								bsSize="large"
								onClick={() => this.props.onSave({ type, mock: true })}
								disabled={!importedSpec || !valid}
							>
								<Glyphicon glyph=" gg icon-GG-checkmark-circle" />
								<span>{this.intl.formatMsg('Save and mock')}</span>
							</Button>
						)}
						<Button
							key="save"
							className="save"
							bsSize="large"
							bsStyle="primary"
							onClick={() => {
								this.props.onSave({
									type,
									specId,
									mock: false
								});
							}}
							disabled={!importedSpec || !valid}
						>
							<Glyphicon glyph=" gg icon-GG-checkmark-circle" />
							<span>{confirmationButtonLabel}</span>
						</Button>
					</PageHeader.Section>
				</PageHeader>
				{shouldRenderImportSummary && currentSpec && (
					<Alert bsStyle="warning">
						{this.intl.formatMsg('Clicking "Update" will replace the current specification. The new specification may introduce breaking changes which need manually resolving.')}
					</Alert>
				)}
				{shouldRenderImportSummary && (
					<ImportSummaryView
						specId={specId}
						specType={type}
						importedSpec={importedSpec}
					/>
				)}
				{(!importedSpec && actionLabel === 'Import') && (
					<Alert bsStyle="info">
						{this.intl.formatMsg('You are about to import a {type} specification that will describe your service as part of the API-first design process', { type: typeData.name })}
					</Alert>
				)}
				{(!importedSpec || valid === false) && (
					<form
						ref={(f) => { this.form = f; }}
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
						}}
					>
						<InputFile
							loading={this.props.loading}
							validationState={this.state.validationState}
							onValidationStateOkayText={this.intl.formatMsg('Retry import')}
							onValidationStateOkay={this.resetValidation}
							name="spec"
							dropActionText={
								this.intl.formatMsg('Drag and drop your {type} file, |browse| your computer', { type: typeData.name })
							}
							accept={typeData.fileTypes.join(',')}
							onFileSelect={this.onFileSelect}
							onUrlSelect={this.onUrlSelect}
						/>
					</form>
				)}
			</div>
		);
	}
}

APIImportView.propTypes = {
	loading: PropTypes.bool,
	importedSpec: PropTypes.any,
	currentSpec: PropTypes.any,
	// object of: { name, fileTypes: [], active }
	apiSpecTypes: PropTypes.object.isRequired,
	type: PropTypes.string.isRequired,
	specId: PropTypes.string,
	valid: PropTypes.bool,
	onCancel: PropTypes.func.isRequired,
	onFileSelect: PropTypes.func.isRequired,
	onUrlSelect: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired
};

export default injectIntl(APIImportView);
